import React, { useState, useEffect } from 'react';
import { X, CheckCircle2, XCircle, Clock, Sparkles } from 'lucide-react';
import apiClient from '../../services/apiClient';

interface ChargeCodeStatusModalProps {
  open: boolean;
  currentChargeCode: any | null;
  type: 'vet' | 'service'; // 👈 added prop
  onClose: () => void;
  onUpdate: () => void;
}

export const STATUS = {
  DEACTIVATED: 2,
  ACTIVE: 1,
  INACTIVE: 0,
};

const statusConfig = {
  [STATUS.ACTIVE]: {
    label: 'Active',
    color: 'from-emerald-500 to-green-600',
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-700',
    icon: CheckCircle2,
    glow: 'shadow-emerald-500/20',
  },
  [STATUS.INACTIVE]: {
    label: 'Inactive',
    color: 'from-amber-500 to-orange-600',
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-700',
    icon: Clock,
    glow: 'shadow-amber-500/20',
  },
  [STATUS.DEACTIVATED]: {
    label: 'Deactivated',
    color: 'from-red-500 to-rose-600',
    bg: 'bg-red-50',
    border: 'border-red-200',
    text: 'text-red-700',
    icon: XCircle,
    glow: 'shadow-red-500/20',
  },
};

export const ChargeCodeStatusModal: React.FC<ChargeCodeStatusModalProps> = ({
  open,
  currentChargeCode,
  type, // 👈 received from parent
  onClose,
  onUpdate,
}) => {
  const [selectedStatus, setSelectedStatus] = useState<number>(STATUS.INACTIVE);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (currentChargeCode) {
      setSelectedStatus(currentChargeCode.is_active ?? STATUS.INACTIVE);
    }
  }, [currentChargeCode]);

  const handleConfirm = async () => {
    if (!currentChargeCode) return;

    setLoading(true);
    try {
      // ✅ Use prop to determine correct endpoint
      const endpoint =
        type === 'vet'
          ? `/v1/resource/ChargeCode/${currentChargeCode.id}`
          : `/v1/resource/ServiceChargeCode/${currentChargeCode.id}`;

      await apiClient(endpoint, {
        method: 'PATCH',
        body: { is_active: selectedStatus },
      });

      onUpdate();
      onClose();
    } catch (err) {
      console.error(err);
      alert('Failed to update charge code status');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  const currentConfig = statusConfig[selectedStatus];
  const StatusIcon = currentConfig.icon;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-in fade-in duration-200"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)' }}
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full animate-in zoom-in-95 duration-300"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`h-2 rounded-t-2xl bg-gradient-to-r ${currentConfig.color}`} />

        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
          disabled={loading}
        >
          <X className="w-5 h-5 text-gray-500" />
        </button>

        <div className="p-8">
          <div className="flex items-center gap-4 mb-6">
            <div className={`p-3 rounded-xl bg-gradient-to-br ${currentConfig.color} shadow-lg ${currentConfig.glow}`}>
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              {/* 👇 dynamic title based on type */}
              <h2 className="text-2xl font-bold text-gray-900">
                Change {type === 'vet' ? 'Vet' : 'Service'} Charge Code Status
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Update status for {currentChargeCode?.charge_code || 'charge code'}
              </p>
            </div>
          </div>

          <div className="space-y-3 mb-8">
            {Object.entries(statusConfig).map(([value, config]) => {
              const Icon = config.icon;
              const isSelected = selectedStatus === Number(value);
              return (
                <button
                  key={value}
                  onClick={() => setSelectedStatus(Number(value))}
                  disabled={loading}
                  className={`
                    w-full p-4 rounded-xl border-2 transition-all duration-200
                    ${isSelected
                      ? `${config.bg} ${config.border} ${config.text} shadow-lg ${config.glow} scale-[1.02]`
                      : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300 hover:shadow-md'}
                    ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                  `}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2 rounded-lg transition-colors duration-200
                      ${isSelected ? `bg-gradient-to-br ${config.color} text-white` : 'bg-gray-100 text-gray-600'}`}
                    >
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="flex-1 text-left">
                      <div className="font-semibold">{config.label}</div>
                      <div className={`text-xs ${isSelected ? 'opacity-80' : 'text-gray-500'}`}>
                        {value === String(STATUS.ACTIVE) && 'Charge code is active'}
                        {value === String(STATUS.INACTIVE) && 'Charge code temporarily inactive'}
                        {value === String(STATUS.DEACTIVATED) && 'Charge code permanently deactivated'}
                      </div>
                    </div>
                    {isSelected && <div className={`w-2 h-2 rounded-full bg-gradient-to-br ${config.color} animate-pulse`} />}
                  </div>
                </button>
              );
            })}
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={loading}
              className="flex-1 px-6 py-3 rounded-xl border-2 border-gray-300 text-gray-700 font-semibold hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={loading}
              className={`
                flex-1 px-6 py-3 rounded-xl font-semibold text-white
                bg-gradient-to-r ${currentConfig.color}
                hover:shadow-lg hover:scale-[1.02]
                active:scale-[0.98]
                transition-all duration-200
                disabled:opacity-50 disabled:cursor-not-allowed
                ${currentConfig.glow}
              `}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                  Saving...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <StatusIcon className="w-5 h-5" />
                  Confirm
                </span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
